import React, { useState } from "react";
import { Plus, Minus } from "lucide-react";

/**
 * FAQ — light hybrid theme. Warm-white section, navy headings, teal accents.
 *
 * Layout: centered intro (eyebrow + title + supporting paragraph), then
 * questions distributed across two columns so the left/right rhythm fills
 * the page instead of leaving a tall blank gutter.
 *
 * Kept the `tone` prop for backwards compatibility — no-op.
 */
export default function FAQ({
  title = "Buyer's guide.",
  faqs = [],
  intro = "The questions every commercial buyer should put on the table before signing for a water system. Can't see yours? Send us a brief — a HidroVerse specialist replies within one business day.",
  // eslint-disable-next-line no-unused-vars
  tone,
  // eslint-disable-next-line no-unused-vars
  eyebrow,
}) {
  const [open, setOpen] = useState(0);

  // Split into left/right columns, top-to-bottom reading order per column.
  const mid = Math.ceil(faqs.length / 2);
  const leftCol = faqs.slice(0, mid);
  const rightCol = faqs.slice(mid);

  const renderItem = (q, a, absoluteIndex) => {
    const isOpen = open === absoluteIndex;
    return (
      <div
        key={absoluteIndex}
        className="border-b"
        style={{ borderColor: "rgba(12,42,64,0.10)" }}
        data-testid={`faq-item-${absoluteIndex}`}
      >
        <button
          onClick={() => setOpen(isOpen ? -1 : absoluteIndex)}
          className="w-full flex items-start justify-between gap-6 py-5 md:py-6 text-left transition-colors hover:opacity-80"
          style={{ color: "#0C2A40" }}
          data-testid={`faq-toggle-${absoluteIndex}`}
        >
          <span
            className="font-display leading-tight"
            style={{
              fontSize: "1.02rem",
              fontWeight: 600,
              letterSpacing: "-0.005em",
              color: "#0C2A40",
            }}
          >
            {q}
          </span>
          <span className="shrink-0 mt-1" style={{ color: "#1FB6C1" }}>
            {isOpen ? <Minus size={18} /> : <Plus size={18} />}
          </span>
        </button>
        <div
          className="overflow-hidden transition-all duration-500 ease-out"
          style={{ maxHeight: isOpen ? "800px" : "0px", opacity: isOpen ? 1 : 0 }}
        >
          <p
            className="pb-6 pr-6 font-light leading-relaxed"
            style={{ fontSize: "0.98rem", color: "#1A1F25" }}
          >
            {a}
          </p>
        </div>
      </div>
    );
  };

  return (
    <section
      className="px-6 md:px-12 lg:px-20 pt-10 md:pt-12 pb-14 md:pb-20"
      style={{ background: "#FAFAF7", color: "#0C2A40" }}
      data-testid="faq-section"
    >
      <div className="max-w-[1200px] mx-auto">
        {/* Centered intro */}
        <div className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
          <h2
            className="font-display mb-4"
            style={{
              fontSize: "clamp(2.2rem, 5vw, 3.8rem)",
              lineHeight: 1.04,
              letterSpacing: "-0.02em",
              fontWeight: 600,
              color: "#0C2A40",
            }}
          >
            {title}
          </h2>
          <p
            className="font-light leading-relaxed"
            style={{ fontSize: "0.98rem", color: "rgba(26,31,37,0.7)" }}
          >
            {intro}
          </p>
        </div>

        {/* Two-column question grid */}
        <div className="grid md:grid-cols-2 gap-x-12 lg:gap-x-16">
          <div className="border-t" style={{ borderColor: "rgba(12,42,64,0.10)" }}>
            {leftCol.map(([q, a], i) => renderItem(q, a, i))}
          </div>
          <div className="border-t" style={{ borderColor: "rgba(12,42,64,0.10)" }}>
            {rightCol.map(([q, a], i) => renderItem(q, a, mid + i))}
          </div>
        </div>
      </div>
    </section>
  );
}
